import React from 'react';
import PropTypes from 'prop-types';
import { useUserLifecycleAnalytics } from '../../hooks/useAnalytics';
import MetricCard from '../dashboard/core/MetricCard';
import MetricSection from '../dashboard/core/MetricSection';
import BarChart from '../ui/BarChart';
import { formatPercent } from '../../utils/format';
import { FILTER_PERIODS } from '../../constants';
import './UserLifecycle.css';

/**
 * User Lifecycle Component
 * Tracks user progression through signup → verification → onboarding → first activity → active user
 */
const UserLifecycle = ({ period = FILTER_PERIODS.ALL }) => {
  const { data, loading, error } = useUserLifecycleAnalytics(period);

  if (error) {
    return (
      <div className="user-lifecycle error">
        <h3>Error loading user lifecycle data</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-lifecycle metric-loading" aria-live="polite" aria-busy="true">
        <div className="loading-spinner" />
        <p>Loading user lifecycle data...</p>
      </div>
    );
  }

  if (!data || !data.lifecycleStages) {
    return (
      <div className="user-lifecycle no-data-message">
        <i className="bi bi-info-circle icon-brand"></i>
        <p>No user lifecycle data available for this period.</p>
      </div>
    );
  }

  const { lifecycleStages, funnel, conversionRates, summary, metrics, reengagedUsers } = data;

  // Funnel metrics
  const funnelMetrics = [
    {
      id: 'signup',
      title: 'Signups',
      value: funnel.signup,
      formatType: 'number',
      color: 'blue'
    },
    {
      id: 'verified',
      title: 'Verified',
      value: funnel.verified,
      formatType: 'number',
      subtitle: formatPercent(conversionRates.signupToVerified, 1),
      color: 'green'
    },
    {
      id: 'onboarded',
      title: 'Onboarded',
      value: funnel.onboarded,
      formatType: 'number',
      subtitle: formatPercent(conversionRates.verifiedToOnboarded, 1),
      color: 'teal'
    },
    {
      id: 'first-activity',
      title: 'First Activity',
      value: funnel.firstActivity,
      formatType: 'number',
      subtitle: formatPercent(conversionRates.onboardedToFirstActivity, 1),
      color: 'purple'
    },
    {
      id: 'active',
      title: 'Active Users',
      value: funnel.active,
      formatType: 'number',
      subtitle: formatPercent(conversionRates.firstActivityToActive, 1),
      color: 'green'
    }
  ];

  // Lifecycle stage metrics
  const stageMetrics = [
    {
      id: 'new-users',
      title: 'New Users',
      value: summary.newUsers,
      formatType: 'number',
      color: 'green'
    },
    {
      id: 'verified',
      title: 'Verified',
      value: summary.verified,
      formatType: 'number',
      color: 'blue'
    },
    {
      id: 'onboarded',
      title: 'Onboarded',
      value: summary.onboarded,
      formatType: 'number',
      color: 'teal'
    },
    {
      id: 'first-activity',
      title: 'First Activity',
      value: summary.firstActivity,
      formatType: 'number',
      color: 'purple'
    },
    {
      id: 'active',
      title: 'Active',
      value: summary.active,
      formatType: 'number',
      color: 'green'
    },
    {
      id: 'at-risk',
      title: 'At-Risk',
      value: summary.atRisk,
      formatType: 'number',
      color: 'orange'
    },
    {
      id: 'churned',
      title: 'Churned',
      value: summary.churned,
      formatType: 'number',
      color: 'red'
    },
    {
      id: 'reengaged',
      title: 'Re-engaged',
      value: summary.reengaged,
      formatType: 'number',
      color: 'cyan'
    }
  ];

  // Key metrics
  const keyMetrics = [
    {
      id: 'avg-time-to-value',
      title: 'Avg Time to Value',
      value: metrics.avgTimeToValue,
      formatType: 'number',
      subtitle: 'days',
      color: 'blue'
    },
    {
      id: 'churn-rate',
      title: 'Churn Rate',
      value: metrics.churnRate,
      formatType: 'number',
      subtitle: '%',
      color: 'red'
    },
    {
      id: 'retention-rate',
      title: 'Retention Rate',
      value: metrics.retentionRate,
      formatType: 'number',
      subtitle: '%',
      color: 'green'
    }
  ];

  // Funnel chart data
  const funnelChartData = [
    { label: 'Signup', value: funnel.signup },
    { label: 'Verified', value: funnel.verified },
    { label: 'Onboarded', value: funnel.onboarded },
    { label: 'First Activity', value: funnel.firstActivity },
    { label: 'Active', value: funnel.active }
  ];

  return (
    <div className="user-lifecycle">
      <div className="lifecycle-summary">
        <MetricCard
          title="Total Users"
          value={summary.totalUsers}
          formatType="number"
          color="blue"
          loading={loading}
        />
      </div>

      <div className="lifecycle-grid">
        <MetricSection
          title="Conversion Funnel"
          icon="bi-funnel"
          description="User progression through key stages"
          metrics={funnelMetrics}
          loading={loading}
        />

        <MetricSection
          title="Key Metrics"
          icon="bi-speedometer2"
          description="Important lifecycle metrics"
          metrics={keyMetrics}
          loading={loading}
        />

        <MetricSection
          title="Lifecycle Stages"
          icon="bi-diagram-3"
          description="Current distribution of users across lifecycle stages"
          metrics={stageMetrics}
          loading={loading}
        />
      </div>

      <div className="funnel-visualization">
        <h3>Funnel Visualization</h3>
        <div className="chart-container">
          <BarChart
            data={funnelChartData}
            label="Users"
            formatType="number"
            color="accent"
            orientation="vertical"
          />
        </div>
      </div>
    </div>
  );
};

UserLifecycle.propTypes = {
  period: PropTypes.string.isRequired
};

export default UserLifecycle;

